import { addDays } from 'date-fns';

import { formatDate, isAfterOrSame, isBeforeOrSame } from '@spa/shared/utils/date';
import { toDate } from '@spa/shared/utils/transformers/contracts';

const FORMAT = 'dd MMMM yyyy';

const MESSAGES = {
  min: moveInOut => `The ${moveInOut} needs to be booked after {{date}}`,
  max: moveInOut =>
    moveInOut === 'move-in'
      ? 'The move-in date must be on or before <span>{{date}}</span>. Select a date in the available range above.'
      : `The ${moveInOut} cannot be booked after {{date}}`
};

export const validAvailabilityRules = (t, language, availabilityRules = [], availableFrom, moveIn, moveOut = null) => {
  if (!availabilityRules || !availabilityRules.length || (!moveIn && !moveOut)) {
    return { isValid: true, error: null };
  }

  const [{ isValid, error, date }] = availabilityRules.map(({ gap }) =>
    (gap?.move_in_out?.items ?? []).reduce(
      (prevAcc, { start, end }) => {
        if (!prevAcc.isValid) {
          return prevAcc;
        }

        const isStartValid = isGapValid(start, moveIn, availableFrom, language);
        const isEndValid = moveOut ? isGapValid(end, moveOut, moveIn, language) : { isValid: true };
        const error = !isStartValid.isValid
          ? { ...isStartValid, error: MESSAGES[isStartValid.type]('move-in') }
          : !isEndValid.isValid
          ? { ...isEndValid, error: MESSAGES[isEndValid.type]('move-out') }
          : {};

        return { ...error, isValid: isEndValid.isValid && isStartValid.isValid };
      },
      { isValid: true }
    )
  );

  return { isValid, error: t(error, { date }) };
};

const isGapValid = (availabilityRange, moveInOut, availableFrom, language) => {
  const { min, max } = availabilityRange || {};
  const isMinValid = min ? isMinOrMaxValid(min, moveInOut, availableFrom, isBeforeOrSame, language) : { isValid: true };
  const isMaxValid = max ? isMinOrMaxValid(max, moveInOut, availableFrom, isAfterOrSame, language) : { isValid: true };
  const error = !isMinValid.isValid
    ? { type: 'min', ...isMinValid }
    : !isMaxValid.isValid
    ? { type: 'max', ...isMaxValid }
    : {};

  return { ...error, isValid: isMaxValid.isValid && isMinValid.isValid };
};

const isMinOrMaxValid = ({ days, fixed_date }, moveInOut, availableFrom, condition, language) => {
  const isDaysValid = !days || condition(addDays(availableFrom, days), moveInOut);
  const isFixedDateValid = !fixed_date || condition(toDate(fixed_date), moveInOut);

  return {
    isValid: isDaysValid && isFixedDateValid,
    date: !isDaysValid
      ? formatDate(addDays(availableFrom, days), language, FORMAT)
      : !isFixedDateValid
      ? formatDate(toDate(fixed_date), language, FORMAT)
      : null
  };
};

export const getMaxMoveInDate = (availableFrom, availabilityRules) => {
  const rule = availabilityRules.find(rule => rule?.gap?.move_in_out?.items?.[0]?.start?.max?.days);
  if (!rule) {
    return null;
  }

  return addDays(availableFrom, rule.gap.move_in_out.items[0].start.max.days);
};
